import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { TESTIMONIALS } from "@/lib/data";

export default function Testimonials() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  const t = TESTIMONIALS[idx];

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Student Stories</div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
            Real dreams, <span className="font-serif italic text-gradient-royal">real results.</span>
          </h2>
        </div>

        <div className="mt-14 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid md:grid-cols-5 gap-8 items-center glass-solid rounded-3xl p-8 md:p-12 border border-border"
            >
              <div className="md:col-span-2 flex flex-col items-center text-center">
                <div className="relative">
                  <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#0F3D8C] blur-md opacity-40" />
                  <img
                    src={t.photo}
                    alt={t.name}
                    className="relative w-32 h-32 rounded-full object-cover border-4 border-white dark:border-[#111827] shadow-xl"
                  />
                </div>
                <div className="mt-5 font-heading font-semibold text-lg">{t.name}</div>
                <div className="text-xs text-[#0F3D8C] dark:text-[#60A5FA] font-medium tracking-wide mt-1">{t.exam}</div>
                <div className="flex gap-0.5 mt-2">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]" />)}
                </div>
              </div>
              <div className="md:col-span-3 relative">
                <Quote className="w-10 h-10 text-[#D4AF37]/40" />
                <p className="mt-4 text-xl sm:text-2xl font-light leading-relaxed tracking-tight">
                  {t.quote}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={() => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
              data-testid="testimonial-prev"
              className="w-11 h-11 rounded-full border border-border hover:bg-[#0F3D8C] hover:text-white hover:border-[#0F3D8C] transition-colors flex items-center justify-center"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`testimonial-dot-${i}`}
                  className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[#D4AF37]" : "w-2 bg-border"}`}
                  aria-label={`Go to ${i + 1}`}
                />
              ))}
            </div>

            <button
              onClick={() => setIdx((i) => (i + 1) % TESTIMONIALS.length)}
              data-testid="testimonial-next"
              className="w-11 h-11 rounded-full border border-border hover:bg-[#0F3D8C] hover:text-white hover:border-[#0F3D8C] transition-colors flex items-center justify-center"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
